import { del, get, keys, set } from 'idb-keyval'
import { PHOTO_CACHE_MAX } from '@mads/shared'
import { api } from './session'

/**
 * The device's copy of the photographs (issue #70).
 *
 * Issue #56 made a photograph a 600px JPEG behind the API: the bucket is
 * private, every read is authorised by a bearer token, and `RecordPhoto` turns
 * the bytes into a blob URL because an `<img src>` sends no headers. All of
 * which works exactly as long as the API is reachable — so in a cellar every
 * picture in the app was a permanent "Loading the photograph…", in the one place
 * somebody wants to check whether the scuffed lantern in front of them is the
 * one on the screen.
 *
 * So the snapshot brings them down with everything else, and this is where they
 * live.
 *
 * **Keyed on the versioned URL the component already asks for.** `RecordPhoto`
 * requests `/api/items/3/image?v=<updatedAt>`, which is what makes a replaced
 * photograph arrive at all (the path is stable by design), so caching on that
 * exact string means the device's copy is exactly as fresh as the browser's
 * would be and no fresher. Nothing here has to know how versioning works.
 *
 * **A copy per record, even where the photograph is shared.** Eight Fresnels
 * showing one catalogue shot have eight URLs — the presenter builds
 * `imageRef('items', $item->getId(), …)` whichever record answered — so they
 * cache eight times. That is half a megabyte to avoid teaching this file how
 * `effectiveImageKey()` resolves, and it is the same trade the server makes when
 * it copies a photograph down per item rather than sharing a key.
 *
 * **The index is separate from the bytes, and that separation is load-bearing.**
 * Twenty megabytes of JPEG lives under one key each; when the photographs were
 * kept per key, the only way to ask "have I got this, and when did I write it"
 * was to read the blob — so a snapshot checking three hundred of them pulled
 * the whole cache through the main thread every quarter of an hour, on the
 * device least able to afford it. One small object answers both questions
 * instead. It is the same reason the queue keeps a photograph's bytes out of
 * its own JSON (`photoStore.ts`).
 */

const KEY_PREFIX = 'mads.photo.'
const INDEX_KEY = 'mads.photo-index'

/** Versioned URL => when its bytes were written. */
type PhotoIndex = Record<string, number>

function keyFor(url: string): string {
  return `${KEY_PREFIX}${url}`
}

async function readIndex(): Promise<PhotoIndex> {
  return (await get<PhotoIndex>(INDEX_KEY)) ?? {}
}

/**
 * The bytes for a photograph, from the device if they are there and from the
 * API otherwise — and written to the device either way, so looking at a lantern
 * once is what puts its picture in the cellar even if the snapshot never got to
 * it.
 *
 * Cache first rather than network first. The versioned URL is the whole of the
 * freshness question, so a hit cannot be stale; and a walk round a building
 * scrolling past two hundred rows should not be two hundred round trips over
 * theatre wifi.
 */
export async function photoBytes(url: string): Promise<Blob> {
  const cached = await get<Blob>(keyFor(url))

  // The blob and not the index, because the bytes are what is being asked for:
  // an index that has drifted ahead of the store answers with a fetch rather
  // than with nothing.
  if (cached !== undefined) return cached

  const blob = await api.imageBlob(url)

  await putPhoto(url, blob)

  return blob
}

export async function putPhoto(url: string, blob: Blob): Promise<void> {
  await set(keyFor(url), blob)
  await set(INDEX_KEY, { ...(await readIndex()), [url]: Date.now() })
}

/**
 * Whether the device already holds this one — answered from the index, which is
 * what the snapshot asks a few hundred times and must not answer by reading a
 * few hundred JPEGs.
 */
export async function hasPhoto(url: string): Promise<boolean> {
  return url in (await readIndex())
}

/**
 * Drops photographs of records the device no longer holds, and then whatever is
 * over the cap.
 *
 * Run after a snapshot, which is the only moment the keep-set is known. The
 * first half is what stops a binned lantern's picture living on the device
 * forever; the second is the bound, and it sheds the least recently written
 * because that is the closest thing to "least likely to be wanted" that a store
 * with no read timestamps can offer.
 */
export async function prunePhotos(keep: ReadonlySet<string>): Promise<void> {
  const index = await readIndex()

  const wanted = Object.keys(index)
    .filter((url) => keep.has(url))
    .sort((a, b) => (index[a] as number) - (index[b] as number))

  const orphans = Object.keys(index).filter((url) => !keep.has(url))
  const excess = wanted.slice(0, Math.max(0, wanted.length - PHOTO_CACHE_MAX))

  const going = new Set([...orphans, ...excess])

  if (going.size === 0) return

  const next: PhotoIndex = {}
  for (const [url, at] of Object.entries(index)) {
    if (!going.has(url)) next[url] = at
  }

  await Promise.all([...going].map((url) => del(keyFor(url))))
  await set(INDEX_KEY, next)
}

/**
 * Forgets every photograph. Sign-out, alongside the query cache — a tablet
 * handed back to the box office should not go on holding pictures of the
 * theatre's kit any more than it holds the asset register.
 *
 * By prefix rather than through the index, deliberately: this is the one place
 * that must leave nothing behind, and a blob the index had lost track of is
 * exactly what would survive a sweep that trusted it.
 */
export async function forgetPhotos(): Promise<void> {
  const stored = await keys()
  const mine = stored.filter(
    (key): key is string => typeof key === 'string' && key.startsWith(KEY_PREFIX),
  )

  await Promise.all([...mine.map((key) => del(key)), del(INDEX_KEY)])
}
